import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Grid, TextField } from '@mui/material'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import { acGetAllPlans, acPostPlan, acPutPlan } from '../../store/actions'
import { PrimaryButton, Alert } from '../default'

import { FormInputText, FormInputCurrency, FormInputNumber } from './formInputs'
import { FormValidationCreatePlan } from './formValidations'

/**
 * @description Renders a form for creating or updating a plan.
 * 
 * @author Maria Eduarda Franke
 * @param {function} props.onClose - The function to close the form.
 * @param {function} props.setShowSnackbar - The function to set the visibility of a snackbar.
 * @param {Object} props.selectedPlan - The selected plan object for updating, if any.
 * @returns {JSX.Element} The rendered CreatePlanForm component.
 */
export const CreatePlanForm = ({ onClose, setShowSnackbar, selectedPlan }) => {

    const defaultValues = {
        name: selectedPlan?.name || '',
        requestsQty: selectedPlan?.requests_qty || 0,
        monthlyCost: selectedPlan?.monthly_cost || 0,
        costPerSurplus: selectedPlan?.cost_per_surplus || 0
    }

    const dispatch = useDispatch()

    const isCreatingPlan = useSelector(state => state.plans.isCreatingPlan)
    const hasErrorCreatedPlan = useSelector(state => state.plans.hasErrorCreatedPlan)

    const isUpdatingPlan = useSelector(state => state.plans.isUpdatingPlan)
    const hasErrorUpdatedPlan = useSelector(state => state.plans.hasErrorUpdatedPlan)

    const [planGroupsIds, setPlanGroupsIds] = useState('')
    const [costPerRequest, setCostPerRequest] = useState('R$ 0,0000')

    const { control, watch, handleSubmit, formState: { errors } } = useForm({
        defaultValues: defaultValues,
        resolver: yupResolver(FormValidationCreatePlan)
    })

    useEffect(() => {
        if (selectedPlan?.planGroups.length > 0) {
            const planGroupsCopy = [...selectedPlan.planGroups]
            const lastGroup = planGroupsCopy.pop()
            const ids = planGroupsCopy.map(group => group.plan_group_id).join(', ')
            setPlanGroupsIds(ids.length > 0 ? `${ids} e ${lastGroup.plan_group_id}` : `${lastGroup.plan_group_id}`)
        }
    }, [selectedPlan])

    useEffect(() => {
        const monthlyCost = watch('monthlyCost')
        const requestsQty = watch('requestsQty')

        if (requestsQty === '0') {
            const value = 0
            const localeString = value.toLocaleString?.('pt-BR', {
                style: 'currency',
                currency: 'BRL',
                minimumFractionDigits: 4,
                maximumFractionDigits: 4
            })
            setCostPerRequest(localeString)
        }

        if (monthlyCost && requestsQty && requestsQty !== '0') {
            const value = monthlyCost / requestsQty
            const localeString = value.toLocaleString?.('pt-BR', {
                style: 'currency',
                currency: 'BRL',
                minimumFractionDigits: 4,
                maximumFractionDigits: 4
            })
            setCostPerRequest(localeString)
        }

        // eslint-disable-next-line
    }, [watch('monthlyCost'), watch('requestsQty')])

    const onSubmit = handleSubmit((data) => {
        if (selectedPlan) {
            dispatch(acPutPlan(selectedPlan.plan_id, data))
                .then(() => {
                    onClose()
                    setShowSnackbar(true)
                    dispatch(acGetAllPlans())
                        .catch(error => console.error(error))
                })
                .catch(error => console.error(error))
        } else {
            dispatch(acPostPlan(data))
                .then(() => {
                    onClose()
                    setShowSnackbar(true)
                    dispatch(acGetAllPlans())
                        .catch(error => console.error(error))
                })
                .catch(error => console.error(error))
        }
    })

    return (
        <Box
            component='form' onSubmit={onSubmit} noValidate
            display='flex' flexDirection='column' gap={2}
        >

            <DevTool control={control} />

            <FormInputText
                name='name'
                label='Nome'
                control={control}
                errors={errors.name}
                fullWidth
                required
            />

            <Grid container display='flex' flexDirection='row' spacing={2}>
                <Grid item xs={12} sm={6}>
                    <FormInputCurrency
                        name='monthlyCost'
                        label='Valor Mensal'
                        decimalScale={2}
                        control={control}
                        errors={errors.monthlyCost}
                        fullWidth
                        required
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormInputNumber
                        name='requestsQty'
                        label='Quantidade de Consultas'
                        control={control}
                        errors={errors.requestsQty}
                        fullWidth
                        required
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        name='costPerRequest'
                        label='Custo por Consulta'
                        value={costPerRequest}
                        fullWidth
                        disabled
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormInputCurrency
                        name='costPerSurplus'
                        label='Custo por Excedente'
                        decimalScale={4}
                        control={control}
                        errors={errors.costPerSurplus}
                        fullWidth
                        required
                    />
                </Grid>
            </Grid>

            {selectedPlan?.planGroups.length > 0 && (
                <Alert severity='warning' message={`Atenção! As alterações realizadas afetarão os seguintes grupos de planos: ${planGroupsIds}.`} sx={{ mt: 2 }} />
            )}

            {(hasErrorCreatedPlan || hasErrorUpdatedPlan) && (
                <Alert severity='error' message={`Erro ao ${selectedPlan ? 'criar' : 'alterar'} plano!`} />
            )}

            <PrimaryButton
                label={selectedPlan ? 'Salvar' : 'Criar Plano'}
                loading={isCreatingPlan || isUpdatingPlan}
                type='submit'
                fullWidth
            />

        </Box>
    )

}

CreatePlanForm.propTypes = {
    onClose: PropTypes.func.isRequired,
    setShowSnackbar: PropTypes.func.isRequired,
    selectedPlan: PropTypes.object
}
